import { IScope, IOnInit, IPromise } from "angular";
import { Chart, ChartConfiguration, registerables } from "chart.js";
import { IQuery } from "../../../../records/server/query";
import {
  vsName as serverUtilityName,
  ServerUtility,
} from "../../../../../app/records/server/serverUtility";
import { ProgressivesContext } from "../../model/progressivesContext";
import { debounce } from "lodash";

Chart.register(...registerables);

interface ProgressiveDailyReading {
  Id: number;
  Section: string;
  Readings: number;
  RequiredReadings: number;
  Complete: boolean;
  isComplete: boolean;
}

export class DailyReadingsGraphController {
  static $inject = ["progressivesContext", "$scope", serverUtilityName];
  public totalReadings: number = 0;
  public totalTrue: number = 0;
  public totalFalse: number = 0;
  private chartConfig: ChartConfiguration | undefined;

  private readonly fetchChartDataDebounce;

  constructor(
    private readonly progressivesContext: ProgressivesContext,
    $scope: IScope,
    private readonly serverUtility: ServerUtility
  ) {
    this.fetchChartDataDebounce = debounce(
      () => this.fetchChartDataInternal(),
      50
    );

    $scope.$watch(
      () => progressivesContext.date,
      (newDate) => {
        if (!newDate) return;
        this.fetchChartDataDebounce();
      }
    );

    $scope.$watch(
      () => progressivesContext.selectedPropertyIds,
      () => {
        this.fetchChartDataDebounce();
      }
    );
  }

  /**
   * Fetches data from the API using HTTP GET
   */
  fetchChartDataInternal(): IPromise<void> {
    return this.serverUtility
      .query<any[]>({
        url: "/api/machines/progressives/ProgressiveDailyMachine/bulk",
        query: {
          ProgressiveDailyPeriodDate: this.progressivesContext.date,
          PropertyIds: this.progressivesContext.selectedPropertyIds,
        },
      })
      .then((queryResults: any) => {
        if (!queryResults) {
          queryResults = [];
        }
        const groupedData = this.processAndGroupData(queryResults.Result);
        this.totalTrue = groupedData.readMachine;
        this.totalFalse = groupedData.notReadMachine;
        this.totalReadings = groupedData.dataLength;
        this.initializeStackedBarChart(groupedData);
      })
      .catch((error) => console.error("Error fetching data from API:", error));
  }

  /**
   * Groups data by 'Section' and calculates true/false percentages
   */
  processAndGroupData(data: any): {
    labels: string[];
    trueData: number[];
    falseData: number[];
    totalTrue: number;
    totalFalse: number;
    dataLength: number;
    readMachine: number;
    notReadMachine: number;
  } {
    const grouped = data.reduce((acc: any, item: any) => {
      const section = item.Section;
      if (!acc[section]) {
        acc[section] = {
          trueTotal: 0,
          falseTotal: 0,
          totalRequired: 0,
          readMachine: 0,
          notReadMachine: 0,
        };
      }
      const isComplete =
        item.Complete === true ||
        item.Complete === "true" ||
        item.Complete === 1;
      acc[section].totalRequired += item.RequiredReadings;

      if (!isComplete) {
        acc[section].falseTotal += item.RequiredReadings;
        acc[section].notReadMachine += 1;
      } else {
        acc[section].trueTotal += item.Readings;
        acc[section].readMachine += 1;
      }

      return acc;
    }, {} as Record<string, { trueTotal: number; falseTotal: number; totalRequired: number; readMachine: number; notReadMachine: number }>);

    const labels: string[] = [];
    const trueData: number[] = [];
    const falseData: number[] = [];
    let totalTrue = 0;
    let totalFalse = 0;
    let readMachine = 0;
    let notReadMachine = 0;

    for (const [section, values] of Object.entries(grouped) as [
      string,
      {
        totalRequired: number;
        trueTotal: number;
        falseTotal: number;
        readMachine: number;
        notReadMachine: number;
      }
    ][]) {
      labels.push(section);

      const percentageTrue =
        values.totalRequired > 0
          ? (values.trueTotal / values.totalRequired) * 100
          : 0;

      const percentageFalse =
        values.totalRequired > 0 ? 100 - percentageTrue : 0;

      trueData.push(percentageTrue);
      falseData.push(percentageFalse);

      totalTrue += values.trueTotal;
      totalFalse += values.falseTotal;
      readMachine += values.readMachine;
      notReadMachine += values.notReadMachine;
    }

    const dataLength = data.length;
    return {
      labels,
      trueData,
      falseData,
      totalTrue,
      totalFalse,
      dataLength,
      readMachine,
      notReadMachine,
    };
  }

  /**
   * Initializes the stacked bar chart
   */
  initializeStackedBarChart(data: {
    labels: string[];
    trueData: number[];
    falseData: number[];
    totalTrue: number;
    totalFalse: number;
  }): void {
    this.chartConfig = {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: "True",
            backgroundColor: "#43b243",
            data: data.trueData,
          },
          {
            label: "False",
            backgroundColor: "blue",
            data: data.falseData,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: "Completion Percentage by Section",
          },
        },
        scales: {
          x: {
            stacked: true,
            title: {
              display: true,
              text: "Sections",
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            title: {
              display: true,
              text: "Percentage (%)",
            },
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    };
  }
}
